import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import { AuthService, TokenRefreshService } from '@ld.consumer-portal/utils';

/**
 * Blocks activation until the stored session is known to be valid.
 *
 * A token that merely exists is not enough: after the tab is closed and reopened
 * it survives in localStorage while already being expired. Letting navigation
 * proceed in that state paints cached borrower data (name, loan number, property
 * address) before the first API call returns 401 and bounces to login.
 *
 * Awaiting here blocks the navigation from committing, so the lazy layout component
 * is never instantiated on a dead session. Note that guards within a single
 * `canActivate` array are subscribed concurrently — Angular resolves their *results*
 * in array order, not their execution — so siblings cannot rely on this having run
 * first. Data fetching lives in `dashboardDataResolver`, which runs in the later
 * `resolveData()` stage and therefore only after every guard has returned `true`.
 */
export const authGuard: CanActivateFn = async (_, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const environment = inject(ENVIRONMENT) as Environment;
  const tokenRefreshService = inject(TokenRefreshService);

  if (environment.mockConfig?.enabled) {
    return true;
  }

  if (!authService.isAuthenticated()) {
    return buildLoginRedirect(router, state, false);
  }

  if (await tokenRefreshService.ensureValidSession()) {
    return true;
  }

  authService.clearAllTokens();
  return buildLoginRedirect(router, state, true);
};

function buildLoginRedirect(router: Router, state: RouterStateSnapshot, sessionExpired: boolean): UrlTree {
  const queryParams: Record<string, string> = {};

  if (state.url !== '/') {
    queryParams['returnUrl'] = state.url;
  }

  if (sessionExpired) {
    queryParams['sessionExpired'] = 'true';
  }

  return router.createUrlTree(['/login'], { queryParams });
}
